import React, { FormEvent, useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Tooltip,
  IconButton,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import styles from "./PessoalForm.module.css";
import styles_button from "../../../styles/Button.module.css";
import styles_general from "../../../styles/General.module.css";
import { profissoes } from "../../../resources/Dados";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import SelectField from "../../atoms/SelectField/SelectField";
import ConditionalField from "../../atoms/ConditionalField/ConditionalField";
import { useCadastro } from "../../../resources/CadastroProvider";
import {
  PersonalData,
  GenderIdentity,
} from "../../../types/Models";
import GenericSwitchTextField from "../../atoms/GenericSwitchTextField/GenericSwitchTextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider";
import MultiSelectField from "../../atoms/MultiSelectField/MultiSelectField";

dayjs.locale("pt-br");

interface Props {
  edit?: boolean;
}

const PessoalForm = ({ edit }: Props) => {
  const {
    personalData,
    setPersonalData,
    disabilities,
    setDisabilities,
    handlePrevStep,
    handleNextStep,
    steps,
    setStepCompletedAtIndex,
    handleUpdateUser,
    loadingUpdate,
  } = useCadastro();

  const [mensagem, setMensagem] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<boolean>(false);
  const [wasOpen, setWasOpen] = useState<boolean>(false);

  const deficienciaOptions = [
    { nome: "Física" },
    { nome: "Auditiva" },
    { nome: "Visual" },
    { nome: "Intelectual" },
    { nome: "Transtorno do Espectro Autista" },
    { nome: "Mental" },
    { nome: "Outras" },
  ];

  const handleSanitizeChange = (value: string, field: keyof PersonalData) => {
    const sanitizedValue = value.replace(/[^a-zA-ZÀ-ÿ\s]/g, "");
    setPersonalData((prev) => ({
      ...prev,
      [field]: sanitizedValue,
    }));
  };

  const handleStructureParticipation = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const sanitizedValue = value.replace(/[^a-zA-ZÀ-ÿ\s]/g, "");
    setPersonalData({ ...personalData, estrutura_participacao: sanitizedValue });
  };

  const handleSwitchChange = (field: keyof PersonalData, checked: boolean) => {
    setPersonalData({
      ...personalData,
      [field]: checked,
    });
  };

  const handleSwitchChangeWithPopup = (
    field: keyof PersonalData,
    checked: boolean,
  ) => {
    setPersonalData((prev) => ({
      ...prev,
      [field]: checked,
      nome_social: checked ? prev.nome_social : "", // Reseta o campo ao desativar
    }));

    if (field === "possui_nome_social" && checked && !wasOpen) {
      setOpen(true);
    }
  };

  const handleChange = (field: keyof PersonalData, value: any) => {
    setPersonalData({ ...personalData, [field]: value });
  };

  const handleIdentidadeGeneroChange = (value: string) => {
    setPersonalData((prev) => ({
      ...prev,
      identidade_genero: value as GenderIdentity,
      identidade_genero_outros: "",
    }));
  };

  const handleProfissaoChange = (value: string) => {
    setPersonalData((prev) => ({
      ...prev,
      profissao: value,
      profissao_outros: value === "Outra" ? prev.profissao_outros : "",
    }));
  };

  const handleDateChange = (value: Dayjs | null) => {
    if (value) {
      setPersonalData({ ...personalData, data_nascimento: value.toDate() });
    }
  };

  const handleDeficienciaChange = (value: string | string[]) => {
    const newValues = Array.isArray(value) ? value : [value]; // Garante que sempre serÃ¡ um array
    setDisabilities(
      newValues.map((nome, index) => ({
        id: index,
        deficiencia: nome,
      })),
    );

    const hasOutras = newValues.includes("Outras");
    if (!hasOutras) {
      setPersonalData((prev) => ({
        ...prev,
        deficiencia_outros: "",
      }));
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    setMensagem("");
    setErrors({});

    const indexStep = steps.findIndex(
      (step) => step.label === "Dados Pessoais",
    );
    if (indexStep !== -1) {
      setStepCompletedAtIndex(indexStep, false);
    }

    const {
      identidade_genero,
      data_nascimento,
      escolaridade,
      profissao,
      profissao_outros,
      deficiencia_outros,
      possui_nome_social,
      nome_social,
      possui_deficiencia,
      cor_raca
    } = personalData;

    const validationErrors: Record<string, string> = {};

    if (!identidade_genero) {
      validationErrors.identidade_genero = "Identidade de Gênero é obrigatória.";
    }
    if (!cor_raca) {
      validationErrors.cor_raca = "Cor/Raça é obrigatória.";
    }

    if (!data_nascimento) {
      validationErrors.data_nascimento = "Data de nascimento é obrigatória.";
    }

    if (!escolaridade) {
      validationErrors.escolaridade = "Escolaridade é obrigatória.";
    }

    if (!profissao) {
      validationErrors.profissao = "Profissão é obrigatória.";
    }
    if (profissao === "Outra" && !profissao_outros?.trim()) {
      validationErrors.profissao_outros = "Informe a profissão.";
    }

    if (data_nascimento) {
      const age = dayjs().diff(dayjs(data_nascimento), "year");
      if (age < 18) {
        validationErrors.data_nascimento = "A idade deve ser maior que 18 anos.";
      } else if (age > 100) {
        validationErrors.data_nascimento = "A idade deve ser válida.";
      }
    }

    if (possui_nome_social && !nome_social) {
      validationErrors.nome_social = "Por favor, informe o nome social.";
    }

    if (possui_deficiencia && disabilities.length === 0) {
      validationErrors.deficiencia = "Por favor, informe a deficiência.";
    }

    const hasDeficienciaOutras = disabilities.some(
      (deficiencia) => deficiencia.deficiencia === "Outras",
    );
    if (possui_deficiencia && hasDeficienciaOutras && !deficiencia_outros?.trim()) {
      validationErrors.deficiencia_outros = "Descreva a deficiência.";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (edit) {
      handleUpdateUser(2);
    } else {
      setStepCompletedAtIndex(indexStep, true);
      setMensagem("");
      handleNextStep();
    }
  };

  const handleClose = () => {
    setOpen(false);
    setWasOpen(true);
  };

  return (
    <div className={styles.container} data-testid="cadastro-pessoal-form">
      <div className={styles.card}>
        <div className={styles.title}>
          Dados Pessoais
        </div>

        <div className={styles.message}>
          {mensagem && <Typography color="error">{mensagem}</Typography>}
        </div>
        
        <div className={styles.lineInput2}>
          <div className={styles.inputGroup}>
            <label className={styles.titleInput}>
              Identidade de Gênero *
            </label>
            <SelectField
              label=""
              variant="standard"
              inputClassName={styles.input2}
              fullWidth
              value={personalData.identidade_genero || ""}
              onChange={handleIdentidadeGeneroChange}
              options={[
                { nome: GenderIdentity.MULHER },
                { nome: GenderIdentity.MULHER_TRANS },
                { nome: GenderIdentity.HOMEM },
                { nome: GenderIdentity.HOMEM_TRANS },
                { nome: GenderIdentity.NAO_BINARIO },
                { nome: GenderIdentity.OUTRO },
              ]}
              required
              error={!!errors.identidade_genero}
              helperText={errors.identidade_genero}
            />
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.titleInput}>
              Cor/Raça *
            </label>
            <SelectField
              label=""
              variant="standard"
              inputClassName={styles.input2}
              value={personalData.cor_raca || ""}
              onChange={(e) => handleChange("cor_raca", e)}
              options={[
                { nome: "Branca" },
                { nome: "Preta" },
                { nome: "Amarela" },
                { nome: "Parda" },
                { nome: "Indígena" },
              ]}
              fullWidth
              error={!!errors.cor_raca}
              helperText={errors.cor_raca}
            />
          </div>
            
          <div className={styles.inputGroup}>
            <label className={styles.titleInput}>
              Data de Nascimento *
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label=""
                value={
                  personalData.data_nascimento
                    ? dayjs(personalData.data_nascimento)
                    : null
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    InputLabelProps: {
                      shrink: true,
                    },
                    error: !!errors.data_nascimento,
                    helperText: errors.data_nascimento,
                    // Utilize a propriedade SX para garantir a precedÃªncia
                    sx: {
                      // 1. Estiliza a borda normal (estado default)
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#5A81FA', // green-500 do Tailwind
                        borderWidth: '1px', // Borda mais grossa
                        borderRadius: '15px',
                      },
                      // 2. Estiliza a borda no hover
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#5A81FA', // green-600 do Tailwind (ou qualquer cor)
                      },
                      // 3. Estiliza o Ã­cone (como antes, para complementar)
                      '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                        color: '#5A81FA', // green-500
                      },
                    },
                  },
                }}
                onChange={handleDateChange}
                format="DD/MM/YYYY"
              />
            </LocalizationProvider>
          </div>
        </div>

        <div>
          <div className={styles.inputGroup}>
            <label className={styles.titleSwtich}>
              Nome Social?
              <Tooltip
              title="Considera-se Nome Social designação pela qual a pessoa travesti
                  ou transexual se identifica e é socialmente reconhecida, e
                  identidade de Gênero dimensão da identidade de uma pessoa que
                  diz respeito à forma como se relaciona com as representações de
                  masculinidade e feminilidade e como isso se traduz em sua
                  prática social, sem guardar relação necessária com o sexo
                  atribuído no nascimento. (Conforme Art.1° do DECRETO Nº 8.727,
                  DE 28 DE ABRIL DE 2016)."
              arrow
            >
              <IconButton>
                <InfoOutlined sx={{ 
                  color: '#5A81FA',
                  fontSize: '22px',
                  alignItems: 'start',
                  justifyContent: 'start',
                }}/>
              </IconButton>
            </Tooltip>
            </label>
            
            <GenericSwitchTextField
              label=""
              value={personalData.possui_nome_social || false}
              onChange={(e) =>
                handleSwitchChangeWithPopup("possui_nome_social", e)
              }
              textFieldLabel="Informe o Nome Social"
              textFieldValue={personalData.nome_social || ""}
              onTextFieldChange={(e) => handleSanitizeChange(e, "nome_social")}
              view="column"
              customClassName={styles.input}
              textFieldError={!!errors.nome_social}
              textFieldHelperText={errors.nome_social}
            />

            <Dialog open={open} onClose={handleClose}>
              <DialogTitle>
                Considera-se Nome Social designação pela qual a pessoa travesti ou transexual se identifica e é socialmente reconhecida, 
                e identidade de gênero dimensão da identidade de uma pessoa que diz respeito à forma como se relaciona com as representações de 
                masculinidade e feminilidade e como isso se traduz em sua prática social, sem guardar relação necessária com o sexo atribuído no nascimento. 
                (Conforme Art. 1º do DECRETO Nº 8.727, DE 28 DE ABRIL DE 2016).
              </DialogTitle>
              <DialogActions>
                <Button onClick={handleClose} color="primary">
                  Fechar
                </Button>
              </DialogActions>
            </Dialog>

          </div>
        </div>

        <div className={styles.lineInput}>
          <div className={styles.inputGroup}>
            <label className={styles.titleInput}>
              Escolaridade *
            </label>
            <SelectField
              label=""
              fullWidth
              value={personalData.escolaridade || ""}
              onChange={(e) => handleChange("escolaridade", e)}
              options={[
                { nome: "Ensino Fundamental Incompleto" },
                { nome: "Ensino Fundamental Completo" },
                { nome: "Ensino Médio Incompleto" },
                { nome: "Ensino Médio Completo" },
                { nome: "Ensino Técnico Incompleto" },
                { nome: "Ensino Técnico Completo" },
                { nome: "Ensino Superior Incompleto" },
                { nome: "Ensino Superior Completo" },
                { nome: "Especialização Incompleta" },
                { nome: "Especialização Completa" },
                { nome: "Mestrado Incompleto" },
                { nome: "Mestrado Completo" },
                { nome: "Doutorado Incompleto" },
                { nome: "Doutorado Completo" },
              ]}
              required
              error={!!errors.escolaridade}
              helperText={errors.escolaridade}
            />
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.titleInput}>
              Profissão *
            </label>
            <ConditionalField
              label=""
              fullWidth
              value={personalData.profissao || ""}
              onChange={handleProfissaoChange}
              textFieldValue={personalData.profissao_outros || ""}
              onTextFieldChange={(value) =>
                handleSanitizeChange(value, "profissao_outros")
              }
              options={profissoes}
              required
              conditionName="Outra"
              condition={personalData.profissao === "Outra"}
              additionalLabel="Digite sua Profissão"
              customClass={styles.input}
              selectError={!!errors.profissao}
              selectHelperText={errors.profissao}
              textError={!!errors.profissao_outros && personalData.profissao === "Outra"}
              textHelperText={errors.profissao_outros}
            />
          </div>
        </div>

        <div>
          <div className={styles.inputGroup}>
            <label className={styles.titleInput}>
              Possui deficiência?
            </label>
            <GenericSwitchTextField
              label=""
              value={personalData.possui_deficiencia || false}
              onChange={(e) => handleSwitchChange("possui_deficiencia", e)}
              view="column"
              customClassName={styles.input}
            >
              {personalData.possui_deficiencia && (
                <>
                  <MultiSelectField
                    label="Informe a deficiência"
                    fullWidth
                    value={disabilities.map((d) => d.deficiencia)}
                    onChange={handleDeficienciaChange}
                    options={deficienciaOptions}
                    required
                    error={!!errors.deficiencia}
                    helperText={errors.deficiencia}
                  />

                  
                </>
              )}
            </GenericSwitchTextField>
          </div>
        </div>

        <div>
          {disabilities.some((deficiencia) => deficiencia.deficiencia === "Outras") && (
            <div className={`${styles_general.mt4}`}>
              <label className={styles.titleInput}>
                Descreva a deficiência (Outras)
              </label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={personalData.deficiencia_outros || ""}
                onChange={(e) =>
                  handleSanitizeChange(e.target.value, "deficiencia_outros")
                }
                error={!!errors.deficiencia_outros}
                helperText={errors.deficiencia_outros}
                InputProps={{
                  className: styles.input,
                  disableUnderline: true,
                }}
              />
            </div>
          )}

        </div>
            
        {personalData.possui_deficiencia && (
          <div>
            <div className={`${styles_general.mt4} ${styles.inputGroup}`}>
              <label className={styles.titleInput}>
                Necessita de estrutura específica para participar das ações? (máx. 250 caracteres)
              </label>
              <TextField
                size="small"
                placeholder=""
                variant="standard"
                autoFocus={false}
                fullWidth
                data-testid="nome"
                value={personalData.estrutura_participacao}
                onChange={handleStructureParticipation}
                inputProps={{ maxLength: 250 }}
                InputLabelProps={{
                  shrink: false,
                }}
                InputProps={{
                  className: styles.input,
                  disableUnderline: true,
                }}
              />
            </div>
          </div>
        )}

        {!edit ? (
          <div className={styles.buttonContainer}>
            <Button 
              variant="contained" // Use 'contained' para botÃµes com cor de fundo
              sx={{
                width: { xs: '136px', sm: '168px' }, 
                height: { xs: '42px', sm: '46px' },
                paddingY: 2, // 8px (MUI spacing unit)
                paddingX: 6, // 24px (MUI spacing unit)
                backgroundColor: '#2C3D8F',
                color: 'white',
                fontWeight: 500, 
                textAlign: 'center', 
                borderRadius: '9999px',
                '&:hover': {
                  backgroundColor: '#4556A8',
                  boxShadow: 'none', 
                },
                transition: 'background-color 200ms ease-in-out', 
              }}
              color="primary"
              onClick={handlePrevStep}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              sx={{
                width: { xs: '136px', sm: '168px' }, 
                height: { xs: '42px', sm: '46px' },
                paddingY: 2, // 8px (MUI spacing unit)
                paddingX: 6, // 24px (MUI spacing unit)
                backgroundColor: '#5A81FA',
                color: 'white',
                fontWeight: 500, 
                textAlign: 'center', 
                borderRadius: '9999px',
                '&:hover': {
                  backgroundColor: '#7A9BFB',
                  boxShadow: 'none', 
                },
                transition: 'background-color 200ms ease-in-out', 
              }}
              className={styles.nextbutton}
              onClick={handleSubmit}
            >
              Próximo
            </Button>
          </div>
        ) : (
          <Button
            variant="contained"
            sx={{
              width: { xs: '136px', sm: '168px' }, 
              height: { xs: '42px', sm: '46px' },
              paddingY: 1, // 8px (MUI spacing unit)
              paddingX: 6, // 24px (MUI spacing unit)
              backgroundColor: '#5A81FA',
              color: 'white',
              fontWeight: 500, 
              textAlign: 'center', 
              borderRadius: '9999px',
              '&:hover': {
                backgroundColor: '#7A9BFB',
                boxShadow: 'none', 
              },
              transition: 'background-color 200ms ease-in-out', 
            }}
            disabled={loadingUpdate} 
            className={`${styles_button.buttonPrimary} ${(loadingUpdate) ? styles_button.buttonLoad : ''} `}
            onClick={handleSubmit}
          >
            {loadingUpdate ? (
              <>
              <div className={styles_button.spinner}></div>
              <span>Salvando...</span>
              </>
            ) : (
            <span>Salvar</span>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PessoalForm;

/*

data de nascimento

<LocalizationProvider dateAdapter={AdapterDayjs}>
  <DatePicker
    label="Data de Nascimento"
    value={
      personalData.data_nascimento
        ? dayjs(personalData.data_nascimento)
        : null
    }
    onChange={handleDateChange}
    format="DD/MM/YYYY"
  />
</LocalizationProvider>














<TextField
              size="small"
              placeholder=""
              variant="standard"
              autoFocus={false}
              fullWidth
              data-testid="nome"
              required
              inputProps={{ maxLength: 150 }}
              InputLabelProps={{
                shrink: false,
              }}
              InputProps={{
                className: styles.input2,
                disableUnderline: true,
              }}
            />
*/

